import { useState, useCallback, useContext, useEffect, useMemo, FC, ReactNode, createContext } from 'react';
import { WebPaths } from './routes/Paths';
import { GlobalContext } from './GlobalContext';
import { Alert, AlertTitle, Button, CssBaseline, Snackbar, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { createAppTheme } from './styles/themes';
import { AuthService } from 'services/AuthService';
import { ColorTheme } from './styles/colorThemes';
import { ThemeMode } from './GlobalContext';
import { getAppVersion, getServerVersionCachedFirst } from './AppVersion';
// import { applyKeyMUI } from "./utils/mui";

const authService = new AuthService();

interface AppThemeContextType {
    toggleMode: () => void;
    setColorTheme: (theme: ColorTheme) => void;
    mode: ThemeMode;
    colorTheme: ColorTheme;
    themeSelection: 'system' | 'light' | 'dark';
    setThemeSelection: (s: 'system' | 'light' | 'dark') => void;
    density: number;
    setDensity: (d: number) => void;
    cornerRadius: number;
    setCornerRadius: (r: number) => void;
    fontFamily: string;
    setFontFamily: (f: string) => void;
}

export const AppThemeContext = createContext<AppThemeContextType>({
    toggleMode: () => { },
    setColorTheme: (_: ColorTheme) => { },
    mode: 'light',
    colorTheme: 'orange',
    themeSelection: 'system',
    setThemeSelection: () => { },
    density: 8,
    setDensity: () => { },
    cornerRadius: 8,
    setCornerRadius: () => { },
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    setFontFamily: () => { },
});

const getAllPermissions = (expiration_minutes = 30) => {
    let permisos = localStorage.getItem('all_permissions');
    let permisos_date = new Date(localStorage.getItem('all_permissions_expire') || '1970-01-01T00:00:00Z');
    if (permisos === null || permisos === 'undefined' || permisos_date < new Date()) {
        authService.permission
            .getPermissions()
            .then((data) => {
                localStorage.setItem('all_permissions', JSON.stringify(data));
                const now = new Date();
                const futureTime = new Date(now.getTime() + expiration_minutes * 60 * 1000); // 30 minutes
                localStorage.setItem('all_permissions_expire', futureTime.toISOString());
            })
            .catch((error) => {
                console.error('Error fetching permissions:', error);
            });
    }
};

declare module 'notistack' {
    interface VariantOverrides {
        formAlert: {
            Form: FC<any>;
            dataForm: any;
        };
        reportComplete: {
            allowDownload: boolean;
        };
    }
}

interface AlertState {
    open: boolean;
    vertical: 'top' | 'bottom';
    horizontal: 'center' | 'left' | 'right';
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
    autoHideDuration: number | null;
    action: ReactNode | null;
    data: any;
}

const App: FC = () => {
    const { i18n } = useTranslation();
    const adapterLocale = i18n.language?.startsWith('es') ? 'es' : 'en';

    const {
        authenticated,
        setAuthenticated,
        openLeft,
        handlerSideLeft,
        openRight,
        handlerSideRight,
        handlerAlert,
        openAlert,
        setOpenAlert,
        alert,
        setAlert,
        themeMode,
        toggleThemes,
        /*language, changeLanguage,*/ openBottom,
        handlerSideBottom,
        hide,
        collapse,
        setCollapse,
        hidden,
        alertState,
        setAlertState,
    } = useContext(GlobalContext);
    const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
        const savedColorTheme = localStorage.getItem('colorTheme');
        const validThemes: ColorTheme[] = ['purple', 'blue', 'green', 'red', 'amber', 'orange'];
        return validThemes.includes(savedColorTheme as ColorTheme) ? (savedColorTheme as ColorTheme) : 'blue';
    });
    const [themeSelection, setThemeSelectionState] = useState<'system' | 'light' | 'dark'>(() => (localStorage.getItem('themeSelection') as 'system' | 'light' | 'dark') || 'system');
    const [systemDark, setSystemDark] = useState<boolean>(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [density, setDensityState] = useState<number>(() => Number(localStorage.getItem('density')) || 8);
    const [cornerRadius, setCornerRadiusState] = useState<number>(() => Number(localStorage.getItem('cornerRadius')) || 8);
    const [fontFamily, setFontFamilyState] = useState<string>(() => localStorage.getItem('fontFamily') || 'Roboto, Helvetica, Arial, sans-serif');
    const [latestVersion, setLatestVersion] = useState<string | null>(null);
    const [hasNewVersion, setHasNewVersion] = useState(false);
    const [currentVersion, setCurrentVersion] = useState<string | null>(null);

    const effectiveMode: 'light' | 'dark' = themeSelection === 'system' ? (systemDark ? 'dark' : 'light') : themeSelection;

    const setColorTheme = (t: ColorTheme) => {
        localStorage.setItem('colorTheme', t);
        setColorThemeState(t);
    };
    const setThemeSelection = (s: 'system' | 'light' | 'dark') => {
        localStorage.setItem('themeSelection', s);
        setThemeSelectionState(s);
    };
    const setDensity = (d: number) => {
        localStorage.setItem('density', String(d));
        setDensityState(d);
    };
    const setCornerRadius = (r: number) => {
        localStorage.setItem('cornerRadius', String(r));
        setCornerRadiusState(r);
    };
    const setFontFamily = (f: string) => {
        localStorage.setItem('fontFamily', f);
        setFontFamilyState(f);
    };

    const theme = createAppTheme(effectiveMode, colorTheme, { density, cornerRadius, fontFamily });

    useEffect(() => {
        setAuthenticated(sessionStorage.getItem('authenticated') === 'true');
        getAllPermissions();
    }, [setAuthenticated]);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
        localStorage.setItem('colorTheme', colorTheme);
    }, [colorTheme]);

    useEffect(() => {
        let isMounted = true;

        const loadInitialVersion = async () => {
            const version = await getAppVersion();

            if (!isMounted) {
                return;
            }

            setCurrentVersion(version);
        };

        loadInitialVersion();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const applyRemoteVersion = (remoteVersion: string | null) => {
            if (!isMounted || typeof remoteVersion !== 'string') {
                return;
            }

            setLatestVersion(remoteVersion);
            setHasNewVersion(remoteVersion !== currentVersion);
        };

        const checkVersion = async () => {
            try {
                const { cached, fresh } = await getServerVersionCachedFirst();

                if (currentVersion === null && typeof cached === 'string') {
                    setCurrentVersion(cached);
                }

                // 1) Respuesta rapida con lo que haya en cache local/CDN.
                applyRemoteVersion(cached);

                // 2) Respuesta fresca para confirmar si hay nueva version desplegada.
                applyRemoteVersion(fresh);
            } catch (error) {
                console.error('Error checking app version:', error);
            }
        };

        checkVersion();
        const minutos = 5
        const intervalId = window.setInterval(checkVersion, minutos * 60 * 1000);

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, [currentVersion]);

    const handlerAlertLocal = useCallback(
        (
            bool: boolean,
            alert: {
                title: string;
                message: string;
                severity: 'error' | 'warning' | 'info' | 'success';
            },
        ) => {
            if (!bool) {
                setOpenAlert(bool);
                setAlert(alert);
            } else {
                setAlert(alert);
                setOpenAlert(bool);
            }
        },
        [setAlert, setOpenAlert],
    );

    const { vertical, horizontal, open, message, severity, autoHideDuration } = alertState as AlertState;

    const contextValue = useMemo(
        () => ({
            authenticated,
            setAuthenticated,
            openLeft,
            handlerSideLeft,
            openRight,
            handlerSideRight,
            handlerAlert,
            handlerAlertLocal,
            themeMode,
            toggleThemes,
            openBottom,
            handlerSideBottom,
            hide,
            collapse,
            setCollapse,
            hidden,
            alertState,
            setAlertState,
        }),
        [
            authenticated,
            setAuthenticated,
            openLeft,
            handlerSideLeft,
            openRight,
            handlerSideRight,
            handlerAlert,
            handlerAlertLocal,
            themeMode,
            toggleThemes,
            openBottom,
            handlerSideBottom,
            hide,
            collapse,
            setCollapse,
            hidden,
            alertState,
            setAlertState,
        ],
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AppThemeContext.Provider
                    value={{
                        toggleMode: () => setThemeSelection(effectiveMode === 'light' ? 'dark' : 'light'),
                        setColorTheme,
                        mode: effectiveMode,
                        colorTheme,
                        themeSelection,
                        setThemeSelection,
                        density,
                        setDensity,
                        cornerRadius,
                        setCornerRadius,
                        fontFamily,
                        setFontFamily,
                    }}
                >
                    {/* @ts-ignore */}
                    <GlobalContext.Provider value={contextValue}>
                        <WebPaths />

                        <Snackbar
                            open={openAlert}
                            autoHideDuration={6000}
                            onClose={() =>
                                handlerAlertLocal(false, {
                                    title: 'title',
                                    message: 'message',
                                    severity: 'success',
                                })
                            }
                        >
                            <Alert
                                onClose={() =>
                                    handlerAlertLocal(false, {
                                        title: 'title',
                                        message: 'message',
                                        severity: 'success',
                                    })
                                }
                                severity={alert?.severity as 'error' | 'warning' | 'info' | 'success'}
                                variant="filled"
                                sx={{ width: '100%' }}
                            >
                                <AlertTitle> {alert?.title} </AlertTitle>
                                {alert?.message}
                            </Alert>
                        </Snackbar>

                        <Snackbar anchorOrigin={{ vertical, horizontal }} open={open} key={vertical + horizontal} autoHideDuration={autoHideDuration}>
                            <Alert severity={severity}>{message}</Alert>
                        </Snackbar>

                        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={hasNewVersion}>
                            <Alert
                                severity='info'
                                action={
                                    <Button color='inherit' size='small' onClick={() => window.location.reload()}>
                                        Recargar
                                    </Button>
                                }
                            >
                                Hay una nueva version de la web disponible.
                                {latestVersion ? ` (${latestVersion})` : ''}
                            </Alert>
                        </Snackbar>
                    </GlobalContext.Provider>
                </AppThemeContext.Provider>
            </ThemeProvider>
        </LocalizationProvider>
    );
};

export default App;
